// components/config-correspondencia/AddCorrespondencia.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import {
    createCorrespondenciaConfig,
    fetchPrecioPlanServicioForCorrespondencia,
    PrecioPlanServicio
} from '../../services/correspondenciaConfigService';

interface AddCorrespondenciaProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (newConfig: any) => void;
}

const AddCorrespondencia: React.FC<AddCorrespondenciaProps> = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState({
        precio_plan_servicio: 0,
        descripcion: '',
        estado: true,
    });
    const [precioPlanesServicios, setPrecioPlanesServicios] = useState<PrecioPlanServicio[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadPrecioPlanesServicios = useCallback(async () => {
        try {
            const precioPlanesData = await fetchPrecioPlanServicioForCorrespondencia();
            const precioPlanesActivos = precioPlanesData.filter(pps => pps.estado);
            setPrecioPlanesServicios(precioPlanesActivos);
        } catch (err) {
            /* Browser diagnostics omitted. */
            setError('Error al cargar los datos necesarios');
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadPrecioPlanesServicios();
            setFormData({
                precio_plan_servicio: 0,
                descripcion: '',
                estado: true,
            });
            setError('');
        }
    }, [isOpen, loadPrecioPlanesServicios]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: name === 'precio_plan_servicio' ? Number(value) : value
            }));
        }
    };

    const formatPrecioPlanServicioName = (pps: PrecioPlanServicio) => {
        return `${pps.servicio_nombre} - ${pps.plan_nombre}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.precio_plan_servicio === 0) {
            setError('Seleccione una combinación de Plan-Servicio');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const dataToSend = {
                precio_plan_servicio: formData.precio_plan_servicio,
                descripcion: formData.descripcion || undefined,
                estado: formData.estado
            };
            const newConfig = await createCorrespondenciaConfig(dataToSend);
            onCreated(newConfig);
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.message || 'Error al crear la configuración');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-4xl mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Nueva Configuración de Correspondencia</h3>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div className="border-b border-gray-200 pb-4">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Configuración Principal</h4>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Plan - Servicio Principal *
                                    </label>
                                    <select
                                        name="precio_plan_servicio"
                                        value={formData.precio_plan_servicio}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value={0}>Seleccionar Plan - Servicio</option>
                                        {precioPlanesServicios.map(pps => (
                                            <option key={pps.id} value={pps.id} title={formatPrecioPlanServicioName(pps)}>
                                                {formatPrecioPlanServicioName(pps)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Estado
                                    </label>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg h-[42px]">
                                        <span className="text-sm text-gray-600">
                                            {formData.estado ? 'Activo' : 'Inactivo'}
                                        </span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="estado"
                                                className="sr-only"
                                                checked={formData.estado}
                                                onChange={handleChange}
                                            />
                                            <div
                                                className={`w-10 h-6 rounded-full transition-colors ${formData.estado ? 'bg-[#c1348b]' : 'bg-gray-300'}`}
                                            >
                                                <div
                                                    className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.estado ? 'translate-x-5' : 'translate-x-0'}`}
                                                ></div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pb-4">
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Descripción</h4>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción de la configuración (opcional):
                                </label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleChange}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Reglas o condiciones del servicio respecto a correspondencia..."
                                />
                                <p className="text-sm mt-1 text-gray-500">
                                    Describe las reglas específicas para la correspondencia de este servicio
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[120px]"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Creando...
                                </span>
                            ) : 'Crear Configuración'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddCorrespondencia;